import { useEffect, useMemo, useState } from "react";
import Chart from "react-apexcharts";
import { api, fmtBRL, fmtInt, fmtBRDateTime } from "@/lib/api";
import { apexBase, buildParams, fmtH, useChartTheme, PALETTE } from "./pcShared";
import { CaretDown, CaretRight } from "@phosphor-icons/react";

function Spark({ data, dark }) {
  // Memoização: opções/séries estáveis entre renders (evita re-render do ApexCharts)
  const options = useMemo(() => ({
    ...apexBase(dark),
    chart: { sparkline: { enabled: true }, background: "transparent" },
    stroke: { curve: "smooth", width: 2 },
    tooltip: { enabled: false },
    colors: [PALETTE[0]],
  }), [dark]);
  const series = useMemo(() => [{ data: (data || []).map((d) => d.count) }], [data]);
  if (!data?.length) return <span className="text-zinc-300 text-xs">—</span>;
  return <Chart type="line" width={90} height={30} series={series} options={options} />;
}

function StationOrders({ status, applied }) {
  const [data, setData] = useState(null);
  const [skip, setSkip] = useState(0);
  const limit = 25;
  useEffect(() => {
    api.get(`/production-reports/by-station/${status}/orders`, { params: { ...buildParams(applied), skip, limit } })
      .then(({ data }) => setData(data));
  }, [status, applied, skip]);
  if (!data) return <div className="p-3 text-xs text-zinc-400 animate-pulse">Carregando pedidos...</div>;
  return (
    <div className="p-2 bg-zinc-50 dark:bg-transparent" data-testid={`pc-station-orders-${status}`}>
      <table className="table w-full text-xs">
        <thead>
          <tr><th>Data/Hora</th><th>Pedido</th><th>Cliente</th><th>Para</th><th>Colaborador</th><th className="text-right">Peças</th><th className="text-right">Valor</th><th>SKUs</th></tr>
        </thead>
        <tbody>
          {data.items.map((r) => (
            <tr key={`${r.order_id}-${r.at}`}>
              <td className="font-mono">{fmtBRDateTime(r.at)}</td>
              <td className="font-mono font-bold">#{r.bling_number}</td>
              <td className="truncate max-w-[180px]">{r.customer}</td>
              <td>{r.to_label}</td>
              <td>{r.collaborator}</td>
              <td className="text-right font-mono">{fmtInt(r.cups)}</td>
              <td className="text-right font-mono">{fmtBRL(r.value)}</td>
              <td className="truncate max-w-[140px] text-zinc-500">{r.skus}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div className="flex items-center justify-between mt-1 text-[10px] text-zinc-500">
        <span>{fmtInt(data.total)} movimentos neste posto</span>
        <span className="flex gap-1">
          <button className="btn !py-0.5 !px-2" disabled={skip === 0} onClick={() => setSkip(Math.max(0, skip - limit))}>‹</button>
          <button className="btn !py-0.5 !px-2" disabled={skip + limit >= data.total} onClick={() => setSkip(skip + limit)}>›</button>
        </span>
      </div>
    </div>
  );
}

export default function StationsTab({ applied }) {
  const dark = useChartTheme();
  const [data, setData] = useState(null);
  const [expanded, setExpanded] = useState(null);

  useEffect(() => {
    setData(null);
    setExpanded(null);
    api.get("/production-reports/by-station", { params: buildParams(applied) }).then(({ data }) => setData(data));
  }, [applied]);

  if (!data) return <div className="tile p-8 text-center text-zinc-400 animate-pulse">Carregando postos...</div>;

  return (
    <div className="tile overflow-x-auto" data-testid="pc-stations-tab">
      <table className="table w-full text-xs">
        <thead>
          <tr>
            <th></th><th>Posto</th><th className="text-right">Movimentos</th><th className="text-right">Pedidos</th>
            <th className="text-right">Peças</th><th className="text-right">SKUs</th><th className="text-right">Valor</th>
            <th className="text-right">T. médio</th><th className="text-right">T. mín</th><th className="text-right">T. máx</th>
            <th className="text-right">Retrab.</th><th className="text-right">Operadores</th><th>Eficiência</th><th>Tendência</th>
          </tr>
        </thead>
        <tbody>
          {data.stations.map((s) => (
            <StationRow key={s.status} s={s} dark={dark} applied={applied}
              expanded={expanded === s.status}
              onToggle={() => setExpanded(expanded === s.status ? null : s.status)} />
          ))}
        </tbody>
      </table>
    </div>
  );
}

function StationRow({ s, dark, applied, expanded, onToggle }) {
  return (
    <>
      <tr className="cursor-pointer hover:bg-zinc-50 dark:hover:bg-zinc-800" onClick={onToggle} data-testid={`pc-station-row-${s.status}`}>
        <td>{expanded ? <CaretDown size={12} /> : <CaretRight size={12} />}</td>
        <td className="font-bold">{s.label}</td>
        <td className="text-right font-mono">{fmtInt(s.movements)}</td>
        <td className="text-right font-mono">{fmtInt(s.orders)}</td>
        <td className="text-right font-mono font-bold">{fmtInt(s.pieces)}</td>
        <td className="text-right font-mono">{s.skus}</td>
        <td className="text-right font-mono">{fmtBRL(s.value)}</td>
        <td className="text-right font-mono">{fmtH(s.avg_hours)}</td>
        <td className="text-right font-mono text-zinc-500">{fmtH(s.min_hours)}</td>
        <td className="text-right font-mono text-zinc-500">{fmtH(s.max_hours)}</td>
        <td className={`text-right font-mono ${s.reworks_out + s.reworks_in > 0 ? "text-yellow-600 font-bold" : ""}`}>{s.reworks_out + s.reworks_in}</td>
        <td className="text-right font-mono">{s.operators_count}</td>
        <td>
          <div className="w-16 h-2 bg-zinc-200 dark:bg-zinc-700 rounded overflow-hidden">
            <div className="h-full bg-[#2B5EFF]" style={{ width: `${s.efficiency_pct}%` }} />
          </div>
          <span className="text-[9px] text-zinc-500">{s.efficiency_pct}%</span>
        </td>
        <td><Spark data={s.sparkline} dark={dark} /></td>
      </tr>
      {expanded && (
        <tr>
          <td colSpan={14} className="!p-0">
            <div className="px-3 py-2 flex flex-wrap gap-1 border-b border-zinc-200 dark:border-zinc-700">
              {s.operators.map((op) => (
                <span key={op.code || op.name} className="text-[10px] px-2 py-0.5 bg-zinc-100 dark:bg-zinc-800 border border-zinc-300 dark:border-zinc-600 rounded">
                  {op.name} · {fmtInt(op.movements)} mov · {fmtInt(op.pieces)} pçs
                </span>
              ))}
            </div>
            <StationOrders status={s.status} applied={applied} />
          </td>
        </tr>
      )}
    </>
  );
}
